import NextImage, { getImageProps } from 'next/image'
import type { TypeCmsImage, TypeCmsMediumVariant } from '@/schema'
import { type FC } from 'react'

// KEEP STRING ORDER FROM LARGEST TO SMALLEST
const VARIANTS_IN_PROJECT = [
  'xl', 'l', 'm', 's',
  'sliderXl', 'sliderL', 'sliderM', 'sliderS'
]

type TypeVariantsKeys = typeof VARIANTS_IN_PROJECT[number]

type TypeSize = { width: number, height: number }
type TypeVectorImage = {
  type?: 'svg'
  src: string,
  alt: string,
  size?: TypeSize,
  content_type?: 'image/svg+xml'
}

type Props = {
  readonly media?: TypeCmsImage | TypeVectorImage,
  readonly lazy?: boolean,
  readonly initialInView?: boolean,
  onClick?: () => void,
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void
  readonly fill?: boolean,
  readonly outRef?: (node?: Element) => void,
  readonly className?: string,
  readonly sizes?: string,
  readonly quality?: number
  readonly ariaHidden?: boolean
  readonly unoptimized?: boolean
}

export const getMediaVariant = (
  obj: Record<string, TypeCmsMediumVariant> | undefined,
  variantNames: TypeVariantsKeys[] = VARIANTS_IN_PROJECT
): TypeCmsMediumVariant | undefined => {
  if (!obj) return undefined
  for (const key of variantNames) {
    if (key in obj) return obj[key]
  }
  return undefined
}

const Picture = ({ media, lazy, quality, ariaHidden }) => {
  const common = { alt: media.alt, sizes: '100vw' }
  const mobileVariant = getMediaVariant(media, [...VARIANTS_IN_PROJECT].reverse())
  const desktopVariant = getMediaVariant(media, VARIANTS_IN_PROJECT)

  const { props: mobileProps } = getImageProps({
    ...common,
    ...mobileVariant.size,
    quality,
    src: mobileVariant.src,
  })

  const { props: desktopProps } = getImageProps({
    ...common,
    ...desktopVariant.size,
    quality,
    src: desktopVariant.src,
  })

  return (
    <picture>
      <source media='(min-width: 768px)' srcSet={desktopProps.src} height={desktopProps.height} width={desktopProps.width} />
      <source media='(max-width: 767px)' srcSet={mobileProps.src} height={mobileProps.height} width={mobileProps.width} />
      <img loading={lazy ? 'lazy' : 'eager'} draggable='false' {...ariaHidden && { 'aria-hidden': ariaHidden }} />
    </picture>
  )
}

function isTypeVectorImage(media: unknown): media is TypeVectorImage {
  return typeof media === 'object' && media !== null && 'type' in media && (media as TypeVectorImage).type === 'svg'
}

const fallBackSize = { width: 200, height: 200 }

const Image = ({ media, lazy, fill, quality, ariaHidden, sizes, unoptimized, className, onLoad }) => {
  const isVector = isTypeVectorImage(media)
  const variant = isVector ? media : getMediaVariant(media, VARIANTS_IN_PROJECT)

  if (!variant?.size?.width || !variant?.size?.height) {
    console.warn('Image component: wrong variant size params for variant.src:', variant.src)
  }

  const sizeData = (!variant?.size?.width || !variant?.size?.height) ? fallBackSize : variant.size

  return (
    <NextImage src={variant.src} priority={!lazy} loading={lazy ? 'lazy' : 'eager'} {...quality !== 75 && { quality }}
      alt={media.alt}
      {...(!fill && {
        height: sizeData.height,
        width: sizeData.width
      })}
      {...sizes && { sizes }}
      {...className && { className }}
      {...ariaHidden && { 'aria-hidden': ariaHidden }}
      {...(unoptimized || isVector) && { unoptimized: true }}
      {...onLoad && { onLoad }}
      draggable='false'
    />
  )
}

const ComponentByType = {
  'single': Image,
  'multi': Picture
}

const multiVariants = (obj: Record<string, unknown>, keys: TypeVariantsKeys[]): boolean => {
  // Count how many keys in the array are present in the object
  const matchedKeys = keys.filter(keyname => keyname in obj)
  // Return true if at least two keys are found
  return matchedKeys.length >= 2
}

export const Img: FC<Props> = ({ media, lazy, quality = 75, fill = false, sizes, ariaHidden = false, unoptimized = false, className = '', onLoad }) => {
  const Component = ComponentByType[multiVariants(media, VARIANTS_IN_PROJECT) ? 'multi' : 'single']

  return <Component media={media} lazy={lazy} quality={quality} fill={fill} sizes={sizes} ariaHidden={ariaHidden} unoptimized={unoptimized} className={className} onLoad={onLoad} />
}
